(() => {
  // 保留已有自定义元素名称，确保主题编辑器中已保存的视频区块继续生效。
  if (customElements.get('community-instagram')) return;

  const LOAD_TIMEOUT_MS = 25000;
  const MOBILE_MODAL_QUERY = '(max-width: 749px)';

  /**
   * 解析后台填写的视频链接，并为第三方平台生成只含播放器的嵌入地址。
   * @param {string} value 后台填写的视频链接
   * @returns {{ provider: string, originalUrl: string, embedUrl?: string } | null}
   */
  function resolveVideoSource(value) {
    try {
      const normalizedValue = value.trim();
      if (!normalizedValue || !/^(https?:\/\/|\/)/i.test(normalizedValue)) return null;
      const url = new URL(normalizedValue, window.location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return null;

      const host = url.hostname.replace(/^(www|m)\./, '');
      const parts = url.pathname.split('/').filter(Boolean);
      if (['youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)) {
        const isYoutubeShorts = host !== 'youtu.be' && parts[0] === 'shorts';
        const videoId = host === 'youtu.be'
          ? parts[0]
          : (url.searchParams.get('v') || (['shorts', 'embed', 'live', 'v'].includes(parts[0]) ? parts[1] : null));
        if (videoId && /^[\w-]+$/.test(videoId)) {
          return {
            provider: isYoutubeShorts ? 'youtube-shorts' : 'youtube',
            originalUrl: url.href,
            // 静音自动播放符合浏览器策略，确保外层播放按钮只需点击一次。
            embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playsinline=1&rel=0`,
          };
        }
      }

      if (['vimeo.com', 'player.vimeo.com'].includes(host)) {
        const videoIndex = parts.findIndex((part) => /^\d+$/.test(part));
        if (videoIndex !== -1) {
          const embedUrl = new URL(`https://player.vimeo.com/video/${parts[videoIndex]}`);
          const accessHash = url.searchParams.get('h') || parts[videoIndex + 1];
          if (accessHash && /^[\w-]+$/.test(accessHash)) embedUrl.searchParams.set('h', accessHash);
          embedUrl.searchParams.set('autoplay', '1');
          embedUrl.searchParams.set('muted', '1');
          return { provider: 'vimeo', originalUrl: url.href, embedUrl: embedUrl.href };
        }
      }

      if (host === 'instagram.com') {
        const postIndex = parts.findIndex((part) => ['p', 'reel', 'tv'].includes(part));
        const postId = parts[postIndex + 1];
        if (postIndex !== -1 && /^[\w-]+$/.test(postId || '')) {
          const originalUrl = `https://www.instagram.com/${parts[postIndex]}/${postId}/`;
          return {
            provider: 'instagram',
            originalUrl,
            embedUrl: `${originalUrl}embed/`,
          };
        }
      }

      return { provider: 'file', originalUrl: url.href };
    } catch {
      return null;
    }
  }

  class CommunityVideo extends HTMLElement {
    /** 初始化链接信息；播放器必须等待用户点击右上角按钮后再创建。 */
    connectedCallback() {
      this.mount = this.querySelector('[data-instagram-mount]');
      this.originalLink = this.querySelector('[data-instagram-link]');
      this.fallback = this.querySelector('[data-instagram-fallback]');
      this.playButton = this.querySelector('[data-video-play]');
      this.uploadedVideoTemplate = this.querySelector('[data-uploaded-video-template]');
      this.source = this.resolveCardVideoSource();
      if (!this.mount || !this.originalLink || !this.fallback) return;

      if (!this.source) {
        this.playButton?.setAttribute('hidden', '');
        this.fallback.hidden = false;
        if (this.dataset.instagramUrl) {
          const note = this.querySelector('[data-instagram-note]');
          if (note) note.textContent = '请填写有效的视频链接。';
          console.warn('[Community Stories Grid] 视频链接格式无效。');
        }
        return;
      }

      this.postUrl = this.source.originalUrl || '';
      this.dataset.videoProvider = this.source.provider;
      if (this.postUrl) {
        this.originalLink.href = this.postUrl;
      } else {
        this.originalLink.removeAttribute('href');
      }
      this.originalLink.hidden = true;
      this.fallback.hidden = true;
      if (this.source.provider !== 'instagram' && this.originalLink.textContent.trim() === 'View on Instagram') {
        this.originalLink.textContent = 'View video';
      }

      if (this.source.provider === 'instagram' && !this.shouldUseMobileModal()) {
        this.prepareInstagramPlayer();
      } else {
        this.onPlay = () => this.renderVideo();
        this.playButton?.addEventListener('click', this.onPlay);
      }
    }

    /**
     * 后台上传视频优先；未上传时再使用第三方链接，保持旧数据可继续播放。
     * @returns {{ provider: string, originalUrl: string, embedUrl?: string } | null}
     */
    resolveCardVideoSource() {
      if (this.uploadedVideoTemplate?.content?.querySelector('video')) {
        return { provider: 'uploaded', originalUrl: this.dataset.instagramUrl || '' };
      }

      return resolveVideoSource(this.dataset.instagramUrl || '');
    }

    /**
     * Instagram 不支持父页面调用播放 API，因此先把官方播放器放到封面下方。
     * 播放器加载完成后，右上角按钮仅保留外观，第一次点击会直接进入 iframe 的视频区域。
     */
    prepareInstagramPlayer() {
      this.clearInstagramActivationListeners();
      this.onWindowBlur = () => {
        setTimeout(() => {
          if (document.activeElement === this.player) this.activateInstagramPlayer();
        }, 0);
      };
      window.addEventListener('blur', this.onWindowBlur);
      this.renderVideo({ preloadOnly: true });
    }

    /** 点击后只加载当前卡片，并停止其他卡片，避免多个第三方播放器同时占用资源。 */
    renderVideo({ preloadOnly = false } = {}) {
      if (!preloadOnly && this.shouldUseMobileModal()) {
        this.openMobileVideoModal();
        return;
      }
      if (this.started || !this.source || !this.mount) return;
      this.started = true;

      if (!preloadOnly) {
        this.stopOtherVideos();
      }

      const videoPlayer = this.createVideoPlayer();
      if (!videoPlayer) {
        this.showVideoFallback();
        return;
      }

      const { player, usesNativeVideo } = videoPlayer;
      this.player = player;
      this.bindPlayerLoading(player, usesNativeVideo, preloadOnly);
      if (this.source.provider !== 'uploaded') player.src = this.source.embedUrl || this.postUrl;
      this.mount.replaceChildren(player);
      this.mount.hidden = false;
      if (!preloadOnly && this.playButton) this.playButton.hidden = true;
      if (usesNativeVideo) this.startNativePlayback(player);
    }

    /**
     * 生成当前来源对应的播放器元素；桌面卡片和移动弹窗共用，避免两套分支行为不一致。
     * @returns {{ player: HTMLVideoElement | HTMLIFrameElement, usesNativeVideo: boolean } | null}
     */
    createVideoPlayer() {
      const usesNativeVideo = ['file', 'uploaded'].includes(this.source.provider);
      const player = this.source.provider === 'uploaded'
        ? this.uploadedVideoTemplate?.content?.querySelector('video')?.cloneNode(true)
        : document.createElement(this.source.provider === 'file' ? 'video' : 'iframe');

      if (!player) {
        this.showVideoFallback();
        return null;
      }

      const cardTitle = this.playButton?.getAttribute('aria-label') || 'Video';
      player.setAttribute('aria-label', cardTitle);

      if (this.source.provider === 'uploaded') {
        player.controls = true;
        player.playsInline = true;
        player.preload = 'none';
      } else if (this.source.provider === 'file') {
        player.controls = true;
        player.playsInline = true;
        player.preload = 'none';
        const poster = this.querySelector('.community-stories__media img');
        if (poster) player.poster = poster.currentSrc || poster.src;
      } else {
        player.title = cardTitle;
        player.allow = 'autoplay; fullscreen; encrypted-media; picture-in-picture';
        player.allowFullscreen = true;
        player.referrerPolicy = 'strict-origin-when-cross-origin';
        if (this.source.provider === 'instagram') {
          // Instagram iframe 内部不可跨域改样式，使用专用类在卡片视口内裁掉平台信息区。
          player.className = 'community-stories__iframe-frame community-stories__instagram-frame';
          player.setAttribute('scrolling', 'no');
          this.onInstagramFocus = () => this.activateInstagramPlayer();
          player.addEventListener('focus', this.onInstagramFocus);
        } else if (this.source.provider === 'youtube-shorts') {
          // Shorts 的真实画面接近 9:16，单独给 iframe 一个竖屏视口，避免卡片宽度变化后挤压。
          player.className = 'community-stories__iframe-frame community-stories__youtube-shorts-frame';
        } else if (this.source.provider === 'youtube') {
          player.className = 'community-stories__iframe-frame community-stories__youtube-frame';
        } else if (this.source.provider === 'vimeo') {
          player.className = 'community-stories__iframe-frame community-stories__vimeo-frame';
        }
      }

      return { player, usesNativeVideo };
    }

    /** 绑定播放器加载状态；加载失败时恢复备用封面和链接。 */
    bindPlayerLoading(player, usesNativeVideo, preloadOnly = false) {
      const readyEvent = usesNativeVideo ? 'loadedmetadata' : 'load';
      const onPlayerReady = preloadOnly
        ? () => this.markInstagramPlayerLoaded()
        : () => this.markVideoReady();
      player.addEventListener(readyEvent, onPlayerReady, { once: true });
      player.addEventListener('error', () => this.showVideoFallback(), { once: true });
      this.playerTimeout = setTimeout(() => this.showVideoFallback(), LOAD_TIMEOUT_MS);
    }

    /** 移动端使用全屏弹窗播放，避免窄卡片挤压第三方播放器。 */
    openMobileVideoModal() {
      if (this.started || !this.source) return;
      this.started = true;
      this.stopOtherVideos();

      const videoPlayer = this.createVideoPlayer();
      if (!videoPlayer) {
        this.showVideoFallback();
        return;
      }

      const { player, usesNativeVideo } = videoPlayer;
      const cardTitle = this.playButton?.getAttribute('aria-label') || 'Video';
      const modal = document.createElement('div');
      modal.className = 'community-stories__video-modal';
      modal.dataset.videoProvider = this.source.provider;
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', cardTitle);

      const panel = document.createElement('div');
      panel.className = 'community-stories__video-modal-panel';

      const closeButton = document.createElement('button');
      closeButton.className = 'community-stories__video-modal-close';
      closeButton.type = 'button';
      closeButton.setAttribute('aria-label', 'Close video');
      closeButton.textContent = '×';

      const frame = document.createElement('div');
      frame.className = 'community-stories__video-modal-frame';
      frame.replaceChildren(player);
      panel.append(closeButton, frame);
      modal.append(panel);
      document.body.append(modal);
      document.body.classList.add('community-stories--modal-open');

      this.mobileModal = modal;
      this.player = player;
      this.onMobileModalClick = (event) => {
        if (event.target === modal) this.closeMobileVideoModal();
      };
      this.onMobileModalKeydown = (event) => {
        if (event.key === 'Escape') this.closeMobileVideoModal();
      };
      modal.addEventListener('click', this.onMobileModalClick);
      closeButton.addEventListener('click', () => this.closeMobileVideoModal(), { once: true });
      document.addEventListener('keydown', this.onMobileModalKeydown);

      this.bindPlayerLoading(player, usesNativeVideo);
      if (this.source.provider !== 'uploaded') player.src = this.source.embedUrl || this.postUrl;
      if (this.playButton) this.playButton.hidden = true;
      if (usesNativeVideo) this.startNativePlayback(player);
      closeButton.focus({ preventScroll: true });
    }

    /** Instagram iframe 就绪后允许点击穿透到内部视频，同时继续显示原封面和按钮。 */
    markInstagramPlayerLoaded() {
      clearTimeout(this.playerTimeout);
      if (!this.isConnected) return;
      this.dataset.instagramPlayerLoaded = '';
      this.instagramFocusTimer = setInterval(() => {
        if (document.activeElement === this.player) this.activateInstagramPlayer();
      }, 100);
      if (this.playButton) {
        this.playButton.tabIndex = -1;
        this.playButton.setAttribute('aria-hidden', 'true');
      }
    }

    /** iframe 获得焦点表示用户已经点击内部视频，此时露出播放器并隐藏封面按钮。 */
    activateInstagramPlayer() {
      if (!this.isConnected || !this.player || !('instagramPlayerLoaded' in this.dataset)) return;
      this.clearInstagramActivationListeners();
      for (const card of document.querySelectorAll('community-instagram[data-embed-ready]')) {
        if (card !== this && typeof card.stopVideo === 'function') card.stopVideo();
      }
      this.dataset.embedReady = '';
      this.fallback.hidden = true;
      if (this.playButton) this.playButton.hidden = true;
    }

    /** 同一次用户点击中开始播放；若浏览器禁止有声播放，则自动静音重试。 */
    startNativePlayback(player) {
      const playAttempt = player.play();
      if (!playAttempt || typeof playAttempt.catch !== 'function') return;
      playAttempt.catch(() => {
        if (!this.isConnected || this.player !== player) return;
        player.muted = true;
        const mutedAttempt = player.play();
        mutedAttempt?.catch(() => this.showVideoFallback());
      });
    }

    /** 播放器加载成功后隐藏封面备用内容。 */
    markVideoReady() {
      clearTimeout(this.playerTimeout);
      if (!this.isConnected) return;
      if (this.mobileModal) {
        this.dataset.mobileModalReady = '';
        return;
      }
      this.dataset.embedReady = '';
      this.fallback.hidden = true;
    }

    /** 关闭当前播放器并恢复封面与播放按钮。 */
    stopVideo() {
      clearTimeout(this.playerTimeout);
      this.closeMobileVideoModal({ restoreButton: false });
      this.clearInstagramActivationListeners();
      this.mount?.replaceChildren();
      if (this.mount) this.mount.hidden = true;
      if (this.playButton) {
        this.playButton.hidden = false;
        this.playButton.removeAttribute('aria-hidden');
        this.playButton.removeAttribute('tabindex');
      }
      if (this.fallback) this.fallback.hidden = true;
      if (this.originalLink) this.originalLink.hidden = true;
      delete this.dataset.embedReady;
      delete this.dataset.instagramPlayerLoaded;
      this.started = false;
      if (this.source?.provider === 'instagram' && this.isConnected && !this.shouldUseMobileModal()) this.prepareInstagramPlayer();
    }

    /** 链接无效或加载失败时恢复封面，并提供原链接作为降级入口。 */
    showVideoFallback() {
      clearTimeout(this.playerTimeout);
      if (!this.isConnected) return;
      this.closeMobileVideoModal({ restoreButton: false });
      this.mount.replaceChildren();
      this.mount.hidden = true;
      this.clearInstagramActivationListeners();
      if (this.playButton) this.playButton.hidden = true;
      this.fallback.hidden = false;
      this.originalLink.hidden = !this.postUrl;
      delete this.dataset.embedReady;
      delete this.dataset.instagramPlayerLoaded;
      const note = this.querySelector('[data-instagram-note]');
      if (note) note.textContent = this.postUrl ? '视频暂时无法加载，可点击下方链接打开。' : '视频暂时无法加载，请检查上传的视频。';
      console.warn('[Community Stories Grid] 视频未能加载，保留原链接。');
    }

    /** 主题编辑器重载区块时清理播放器和监听。 */
    disconnectedCallback() {
      clearTimeout(this.playerTimeout);
      this.closeMobileVideoModal({ restoreButton: false });
      this.clearInstagramActivationListeners();
      this.playButton?.removeEventListener('click', this.onPlay);
      this.mount?.replaceChildren();
      delete this.dataset.embedReady;
      delete this.dataset.instagramPlayerLoaded;
      this.started = false;
    }

    /** 清理跨区块重载时注册的 Instagram 焦点监听。 */
    clearInstagramActivationListeners() {
      clearInterval(this.instagramFocusTimer);
      window.removeEventListener('blur', this.onWindowBlur);
      this.player?.removeEventListener('focus', this.onInstagramFocus);
      this.instagramFocusTimer = undefined;
      this.onWindowBlur = undefined;
      this.onInstagramFocus = undefined;
    }

    /** 移动端断点判断集中在一个方法里，便于 Shopify 主题编辑器切换预览尺寸。 */
    shouldUseMobileModal() {
      return window.matchMedia(MOBILE_MODAL_QUERY).matches;
    }

    /** 播放前停止其它已打开的视频和弹窗，避免多个声音同时播放。 */
    stopOtherVideos() {
      for (const card of document.querySelectorAll('community-instagram[data-embed-ready], community-instagram[data-mobile-modal-ready]')) {
        if (card !== this && typeof card.stopVideo === 'function') card.stopVideo();
      }
    }

    /** 关闭移动端弹窗并销毁播放器，确保声音停止。 */
    closeMobileVideoModal({ restoreButton = true } = {}) {
      if (!this.mobileModal) return;
      clearTimeout(this.playerTimeout);
      document.removeEventListener('keydown', this.onMobileModalKeydown);
      this.mobileModal.removeEventListener('click', this.onMobileModalClick);
      this.clearInstagramActivationListeners();
      if (this.player?.tagName === 'VIDEO') {
        this.player.pause();
        this.player.removeAttribute('src');
        this.player.load();
      }
      this.mobileModal.remove();
      document.body.classList.remove('community-stories--modal-open');
      delete this.dataset.mobileModalReady;
      this.mobileModal = undefined;
      this.onMobileModalClick = undefined;
      this.onMobileModalKeydown = undefined;
      this.player = undefined;
      this.started = false;
      if (restoreButton && this.playButton) this.playButton.hidden = false;
    }
  }

  customElements.define('community-instagram', CommunityVideo);
})();
